import React from 'react';
import { connect } from 'react-redux';
import mapboxgl from 'mapbox-gl';
import polyline from '@mapbox/polyline';
import { bindActionCreators } from 'redux';

import {updateFormField, fetchDirections} from '../../actions';

class Map extends React.Component {
    constructor(props) {
        super(props);
        this.map = null;
        this.tripLayerIDs = [];
        this.startMarker = null;
        this.endMarker = null;
        this.tripBounds = [];
    }
    componentDidMount() {
        mapboxgl.accessToken = this.props.config.mapboxToken;

        this.map = new mapboxgl.Map({
            container: this.mapContainer,
            style: this.props.config.mapboxStyle,
            center: [-118.2187, 37.7032],
            minzoom: 10
        });
        const {swBounds, neBounds} = this.props.config;
        const bounds = [[swBounds.lng, swBounds.lat],[neBounds.lng, neBounds.lat]];
        this.map.fitBounds(bounds);
        this.map.addControl(new mapboxgl.NavigationControl());
        this.map.addControl(new mapboxgl.FullscreenControl());

        // Initialize map markers
        const startMarkerEl = document.createElement('div');
        startMarkerEl.className = 'marker start-marker';
        const endMarkerEl = document.createElement('div');
        endMarkerEl.className = 'marker end-marker';
        this.startMarker =  new mapboxgl.Marker({
            element: startMarkerEl,
            anchor: 'bottom',
            draggable: true
        }).on('dragend', () => {
            this.updateOnMarkerDrag(this.startMarker, 'fromPlace');
        });
        this.endMarker =  new mapboxgl.Marker({
            element: endMarkerEl,
            anchor: 'bottom',
            draggable: true
        }).on('dragend', () => {
            this.updateOnMarkerDrag(this.endMarker, 'toPlace');
        });
        this.updateMarkers();
    }

    componentWillUnmount() {
        this.map.remove();
    }

    componentDidUpdate(prevProps) {
        if (this.props.currentTrip !== prevProps.currentTrip) {
            this.drawTrip();
        }
        if ((this.props.from !== prevProps.from) || (this.props.to !== prevProps.to)) {
            this.updateMarkers();
        }
    }

    drawTrip() {
        if (this.tripLayerIDs && this.tripLayerIDs.length > 0) {
            this.tripLayerIDs.forEach(id => this.map.removeLayer(id));
        }
        if (this.props.currentTrip === null) {
            // We just needed to remove the previous layer(s)
            this.tripLayerIDs = [];
            return;
        }
        const { legs } = this.props.currentTrip;
        const newLayerIDs = legs.map(leg => {
            const { legGeometry } = leg;
            const layerID = legGeometry.length > 0 ? legGeometry.points : leg.from.stopId;
            if (!this.map.getSource(layerID)) {
                const geoJSON = polyline.toGeoJSON(legGeometry.points);
                if (leg.from.boardAlightType === 'DEVIATED' || leg.to.boardAlightType === 'DEVIATED') {
                    let pt1;
                    let pt2;
                    if (leg.from.boardAlightType === 'DEVIATED') {
                        pt1 = leg.from;
                        pt2 = geoJSON.coordinates[0];
                    } else {
                        pt1 = leg.to;
                        pt2 = geoJSON.coordinates[geoJSON.coordinates.length - 1];
                    }
                    this.map.addSource(layerID, {
                        'type': 'geojson',
                        'data': {
                            'type': 'FeatureCollection',
                            'features': [
                                {
                                    'type': 'Feature',
                                    'properties': {},
                                    'geometry': geoJSON
                                },
                                {
                                    'type': 'Feature',
                                    'properties': { 'route-type': 'deviation'},
                                    'geometry': {
                                        'type': 'LineString',
                                        'coordinates': [
                                            [pt1.lon, pt1.lat],
                                            pt2
                                        ]
                                    }
                                }
                            ]
                        }
                    });
                } else {
                    this.map.addSource(layerID, {
                        'type': 'geojson',
                        'data': {
                            'type': 'Feature',
                            'properties': {},
                            'geometry': geoJSON                                
                        }
                    });
                }
            }
            this.map.addLayer({
                'id': layerID,
                'type':'line',
                'source': layerID,
                'layout': {
                    'line-join':'round',
                    'line-cap': 'round'
                },
                'paint': this.getLegStyle(leg)
            }, 'waterway-label');
            return layerID;
        });
        this.tripLayerIDs = newLayerIDs;
    }

    getLegStyle(leg) {
        if (leg.mode === 'WALK') {
            return {
                'line-color': '#333',
                'line-width': 4,
                'line-dasharray': [0.05,1.4]
            };
        }
        if (leg.mode === 'BUS') {
            let routeColor = '#000';
            if (leg.routeColor) {
                routeColor = leg.routeColor;
                if (!routeColor.startsWith('#') ) {
                    routeColor = '#' + routeColor;
                }
            }
            return {
                'line-color': routeColor,
                'line-width': 6,
                'line-opacity': ['case', ['==', 'deviation', ['get', 'route-type']],
                    0.3,
                    1
                ]
            };
        }
        return {
            'line-color': '#666',
            'line-width': 6
        };
    }

    zoomToTrip(coords) {
        const lngs = coords.map(coords => coords[0]);
        const lats = coords.map(coords => coords[1]);
        const swCorner = [
            Math.min(...lngs),
            Math.min(...lats)
        ];
        const neCorner = [
            Math.max(...lngs),
            Math.max(...lats)
        ];
        const localBounds = [swCorner, neCorner];
        this.map.fitBounds(localBounds, {padding: 100});
    }

    updateOnMarkerDrag(marker, field) {
        const newCoords = marker.getLngLat();
        const coordString = newCoords.lat.toString() + ',' + newCoords.lng.toString();
        console.log(coordString);
        
        this.props.updateFormField({
            target: {
                name: field,
                value: coordString
            }
        });
        this.props.fetchDirections(this.props.form.fields);
    }

    updateMarkers() {
        let start = null;
        let end = null;
        if (this.props.from) {
            start = this.props.from.split(',').map(parseFloat).reverse();
            this.startMarker.setLngLat(start).addTo(this.map);
        }
        if (this.props.to) {
            end = this.props.to.split(',').map(parseFloat).reverse();
            this.endMarker.setLngLat(end).addTo(this.map);
        }
        if (start && end) {
            this.zoomToTrip([start, end]);
            return;
        }
        if (start) {
            this.map.setCenter(start);
            this.map.setZoom(15);
            return;
        }
        if (end) {
            this.map.setCenter(end);
            this.map.setZoom(15);
            return;
        }
    }
    

    render() {
        return (
            <div className="otp-map-wrapper">
                {!this.props.currentTrip &&
                    <div className="otp-map-center-overlay">
                        Select a trip to see it on the map
                    </div>
                }
                <div className="otp-map-container" ref={el => this.mapContainer = el} />
            </div>
        );
    }
}

// Have to be carefull here, since currentTrip is an index
// that can be equal to 0
const mapStateToProps = state => {
    const {trips, currentTrip} = state.directions;
    const {fields} = state.form;
    const itinerary = currentTrip === null ? null : trips[currentTrip];
    return {
        config: state.config,
        currentTrip: itinerary,
        from: fields.fromPlace.value,
        to: fields.toPlace.value,
        form: state.form
    };
};
const mapDispatch = dispatch => {
    return bindActionCreators({updateFormField, fetchDirections}, dispatch);
};

export default connect(mapStateToProps, mapDispatch)(Map);